import type { ConflictResult, ConflictRule, ConflictRuleContext, PersonSkillValidityRow } from '../conflict.types';
import type { ScheduleMap } from '../../models/expanded-schedule.model';
import { fromStartEnd, isValidOnDate } from '@zag/common/validity';

export class SkillExpiredConflictRule implements ConflictRule {
  readonly type = 'skill_expired' as const;

  detect(schedule: ScheduleMap, _events: unknown[], context?: ConflictRuleContext): ConflictResult[] {
    const personSkills = context?.personSkills;
    const shiftRequiredSkills = context?.shiftRequiredSkills;
    if (!personSkills || !shiftRequiredSkills) return [];

    const skillNames = context?.skillNames;
    const results: ConflictResult[] = [];

    for (const [date, daySchedule] of Object.entries(schedule)) {
      for (const [personId, unitIds] of Object.entries(daySchedule)) {
        const skills = personSkills.get(personId);
        if (!skills) continue;

        for (const unitId of unitIds) {
          this.checkSkillExpiry(results, date, personId, unitId, skills, shiftRequiredSkills, skillNames);
        }
      }
    }

    return results;
  }

  private checkSkillExpiry(
    results: ConflictResult[],
    date: string,
    personId: string,
    unitId: string,
    personSkillList: PersonSkillValidityRow[],
    shiftRequiredSkills: Map<string, string[]>,
    skillNames?: Map<string, string>
  ): void {
    const requiredSkillIds = shiftRequiredSkills.get(unitId);
    if (!requiredSkillIds || requiredSkillIds.length === 0) return;

    for (const requiredSkillId of requiredSkillIds) {
      const personSkill = personSkillList.find(ps => ps.skillId === requiredSkillId);
      // Only flag expired skills, not missing skills (that's a different concern)
      if (!personSkill) continue;

      const active = isValidOnDate(
        fromStartEnd({ startDate: personSkill.acquiredDate ?? null, endDate: personSkill.expiryDate, rrule: personSkill.rrule }),
        date
      );
      if (!active) {
        const skillName = skillNames?.get(requiredSkillId) ?? 'Expired skill';
        results.push({
          type: 'skill_expired',
          date,
          personId,
          eventLabel: skillName,
          calendarId: '',
          shiftUnitId: unitId,
        });
        return;
      }
    }
  }
}
